import { createSignal, Show, type Component } from 'solid-js';

import { fetchExport } from '../lib/api.js';
import type { PlanStatus } from '../lib/types.js';

/**
 * Aging and ledger CSV downloads (SPEC FR-10 a,c).
 *
 * The endpoints existed and the plan table sold "CSV exports" from Starter, but nothing in the UI
 * ever called them — the same advertise-what-you-cannot-deliver problem as the plan feature flags,
 * except here the capability was real and only the door was missing.
 *
 * The file is fetched with the session token and handed over as a Blob rather than linked directly:
 * a plain <a href> navigation carries no Authorization header, so the export endpoint would answer
 * 401 and the merchant would get a downloaded error page.
 */
export const Exports: Component<{ plan: PlanStatus }> = (props) => {
  const [busy, setBusy] = createSignal<'aging' | 'ledger' | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  const included = (): boolean => {
    return props.plan.tiers.find((tier) => tier.current)?.includes.agingExports ?? false;
  };

  async function download(kind: 'aging' | 'ledger'): Promise<void> {
    setBusy(kind);
    setError(null);
    try {
      const blob = await fetchExport(kind);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `duebook-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoked on the next tick: revoking synchronously can cancel the download in some browsers
      // before it has read the blob.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (caught) {
      setError(
        `Could not build the export: ${String(caught)}. Try again, or email support@duebook.app ` +
          'and a human will reply within one business day.',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <s-section heading="Exports">
      <s-stack direction="block" gap="base">
        <Show
          when={included()}
          fallback={
            <s-text tone="subdued">
              CSV exports are included from the Starter plan. Your ledger keeps recording either way
              — only the download is paused.
            </s-text>
          }
        >
          <s-text tone="subdued">
            Aging is the summary your accountant asks for; the ledger is every entry behind it.
          </s-text>
          <s-stack direction="inline" gap="base">
            <s-button
              variant="secondary"
              loading={busy() === 'aging' ? 'true' : undefined}
              disabled={busy() !== null ? 'true' : undefined}
              onClick={() => void download('aging')}
            >
              Download aging CSV
            </s-button>
            <s-button
              variant="secondary"
              loading={busy() === 'ledger' ? 'true' : undefined}
              disabled={busy() !== null ? 'true' : undefined}
              onClick={() => void download('ledger')}
            >
              Download ledger CSV
            </s-button>
          </s-stack>
        </Show>
        <Show when={error()}>
          <s-banner tone="critical" dismissible>
            <s-paragraph>{error()}</s-paragraph>
          </s-banner>
        </Show>
      </s-stack>
    </s-section>
  );
};
